<script lang="ts">
    import Modal from "@/components/Modal.svelte";
    import RelatedSingle from "@/components/Inputs/RelatedSingle.svelte";
    import {Button} from "@/components/ui/button";
    import Input from "@/components/Form/Inputs/Input.svelte";
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import FormBlock from "@/components/Form/FormBlock.svelte";
    import type Blocks from "@/schemas/public/Blocks";
    import type {Field} from "@/types/types";
    import {toast} from "svelte-sonner";
    import {useForm, router} from "@inertiajs/svelte";
    import Checkbox from "@/components/Inputs/Checkbox.svelte";
    import Select from "@/components/Inputs/Select.svelte";
    import Textarea from "@/components/Inputs/Textarea.svelte";
    import MultiRelated from "@/components/Inputs/MultiRelated.svelte";
    import CustomerTable from "@/components/customers/CustomerTable.svelte";
    import {SvelteMap} from "svelte/reactivity";
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import DateOnly from '@/components/Inputs/Date.svelte';
    import AddressLookup from "@/components/Inputs/AddressLookup.svelte";
    import { parseFieldOptions } from '@/lib/fieldUtils';
    import { transformToNestedStructure } from '@/lib/formTransformers';
    import { createDateDurationSync } from '@/lib/dateDurationCalculator.svelte';

    type Props = {
        blocks: (Blocks & { fields: Field[] })[];
        show: boolean;
        relatedRecordId?: string | null;
        relatedRecordLabel?: string | null;
        initialValues?: Record<string, any>;
    };

    let {blocks, show = $bindable(), relatedRecordId, relatedRecordLabel, initialValues}: Props = $props();

    let createFormInner: Record<string, any> = {}
    for (const block of blocks) {
        for (const field of block.fields) {
            // Initialize checkboxes with false instead of null
            if (field.ui_type === 'checkbox') {
                createFormInner[field.column_name] = initialValues?.[field.column_name] === true;
            } else {
                createFormInner[field.column_name] = initialValues?.[field.column_name] ?? null;
            }
        }
    }

    createFormInner['customer_id'] = relatedRecordId ?? null;
    let createForm = $state(useForm(createFormInner));

    // Additional hire date ↔ duration auto-calculation
    createDateDurationSync({
        getStartDate: () => $createForm.additional_hire_start_date,
        getEndDate: () => $createForm.additional_hire_end_date,
        getDuration: () => $createForm.additional_hire_duration?.toString(),
        setEndDate: (date) => { $createForm.additional_hire_end_date = date; },
        setDuration: (duration) => { $createForm.additional_hire_duration = duration; },
    });

    let isSelectCustomerOpen = $state(false);
    let customerLabel: string | null = $state(relatedRecordLabel ?? null);
    let selectedRelatedCustomers = $state(new SvelteMap<string, string>());

    let isSelectPrimaryCustomerOpen = $state(false);
    let primaryCustomerLabel: string | null = $state(relatedRecordLabel ?? null);

    function close() {
        show = false;
    }

    // Handle address selection from lookup
    function handleAddressSelect(address: {
        line_1: string;
        line_2: string;
        city: string;
        postcode: string;
        county: string;
    }) {
        $createForm.address_line_one = address.line_1;
        $createForm.address_line_two = address.line_2;
        $createForm.city = address.city;
        $createForm.postcode = address.postcode;
        $createForm.county = address.county;
    }

    function submit(e) {
        $createForm.processing = true;
        e.preventDefault();

        // Transform flat form data to nested structure
        const transformedData = transformToNestedStructure($createForm.data());

        // Use router.post to send the transformed data (like CreateRecordModal does)
        router.post(route('jobs.store'), transformedData, {
            onSuccess: () => {
                close();
                $createForm.reset();
                toast.success("Job created successfully!");
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error("Error creating job. " + message as string);
                }
                $createForm.processing = false;
            },
            onFinish: () => {
                $createForm.processing = false;
            }
        });
    }

</script>
{#if show}
    <Modal title="Add New Job" onClose={close}>
        <FormContainer>
            <form onsubmit={submit}>
                {#each blocks as block (block.id)}
                    <FormBlock label={block.label}>
                        {#if block.label === 'Site Information'}
                            <AddressLookup onAddressSelect={handleAddressSelect} />
                        {/if}
                        {#each block.fields as field (field.id)}
                            {#if field.editable && field.show_in_create !== false}
                                {#if field.ui_type === 'related'}
                                    <RelatedSingle
                                        label={field.label}
                                        bind:value={$createForm[field.column_name]}
                                        error={$createForm.errors[field.column_name]}
                                        bind:isOpen={isSelectPrimaryCustomerOpen}
                                        bind:valLabel={primaryCustomerLabel}
                                        required={field.required}
                                    >
                                        <CustomerTable
                                            view="related"
                                            onRowClick={(row) => {
                                                $createForm.is_primary = row?.id ?? null;
                                                primaryCustomerLabel = row?.name ?? '';
                                                isSelectPrimaryCustomerOpen = false;
                                            }}
                                        />
                                    </RelatedSingle>
                                {:else if field.ui_type === 'multi_related'}
                                    <MultiRelated
                                        label={field.label}
                                        bind:value={$createForm[field.column_name]}
                                        error={$createForm.errors[field.column_name]}
                                        bind:isOpen={isSelectCustomerOpen}
                                        bind:valLabel={customerLabel}
                                    >
                                        <CustomerTable
                                            view="multi_related"
                                            onRowClick={(row) => {}}
                                            bind:selectedCustomers={selectedRelatedCustomers}
                                            onSubmitClick={() => {
                                                $createForm.customers = selectedRelatedCustomers.keys().toArray();
                                                customerLabel = Array.from(selectedRelatedCustomers.values()).join(", ");
                                                isSelectCustomerOpen = false;
                                            }}
                                        />
                                    </MultiRelated>
                                {:else if field.ui_type === 'checkbox'}
                                    <Checkbox
                                        bind:checked={$createForm[field.column_name]}
                                        label={field.label}
                                        error={$createForm.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'select'}
                                    <Select
                                        {field}
                                        bind:value={$createForm[field.column_name]}
                                        options={parseFieldOptions(field)}
                                        error={$createForm.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'textarea'}
                                    <Textarea
                                        label={field.label}
                                        required={field.required}
                                        bind:value={$createForm[field.column_name]}
                                        error={$createForm.errors[field.column_name]}
                                        {field}
                                    />
                                {:else if field.ui_type === 'date'}
                                    <DateOnly
                                        label={field.label}
                                        bind:value={$createForm[field.column_name]}
                                        error={$createForm.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'datetime'}
                                    <DateTime
                                        {field}
                                        bind:value={$createForm[field.column_name]}
                                        error={$createForm.errors[field.column_name]}
                                    />
                                {:else}
                                    <Input
                                        {field}
                                        bind:value={$createForm[field.column_name]}
                                        error={$createForm.errors[field.column_name]}
                                        onkeydown={() => {}}
                                    />
                                {/if}
                            {/if}
                        {/each}
                    </FormBlock>
                {/each}

                <div class="mt-4 flex justify-end gap-2">
                    <Button type="button" onclick={close}>Cancel</Button>
                    <Button type="submit" disabled={$createForm.processing}>
                        {#if $createForm.processing}Saving...{:else}Save{/if}
                    </Button>
                </div>
            </form>
        </FormContainer>
    </Modal>
{/if}
